import React, { createContext, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import Modal from '@components/styled-UI/Modal';

import PreLoader from '@images/svg-icons/preLoader.svg';

import { RootState } from 'src/store/reducers';

import { getLabelFallback, getMockedFile } from '@utils/utils';
import { GlobalOrderDetailsPayload, IGlobalOrderDetails, IOnlineOrderHistory, } from '@interfaces/myPurchasesInterfaces';
import {
    handleContactLensesReorder,
    mapGlobalOrderDetails,
    mapGlobalOrderDetailsImages,
    mapGlobalOrderDetailsStatus,
} from '@utils/pages-utils/MyPurchasesUtils';
import URLS from '@utils/urlList';
import axios, { AxiosRequestConfig } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import { getStringFromDate } from '@utils/dateUtils';
import BoxImage from '@components/styled-UI/BoxImage';
import OnlineOrder from '@images/svg-icons/onlinePrescription.svg'
import Error from '@images/svg-icons/functionalError.svg'

interface Props {
    isOpenDeleteModal?: boolean;
    isSubmitting?: boolean;
    onModalSubmit?: () => void;
    onCloseModal?: () => void;
    order;
}

const WrapperSummary = ({
    isOpenDeleteModal = false,
    isSubmitting = false,
    onModalSubmit,
    onCloseModal,
    order,
}: Props): JSX.Element => {
    const isMobile = useSelector((state: RootState) => state.common.isMobile);
    const config = useSelector((state: RootState) => state.common.config);
    const [images, setImages] = useState(null);
    const [status, setStatus] = useState(null);
    const MAX_IMAGE = !isMobile ? 4 : 2;
    const [orderItemsAnalytics, setOrderItemsAnalytics] = useState(null);

    const navigate = useNavigate();

    const dispatch = useDispatch();
    const isCTEnabledTO = config.isCTEnabledTO;

    useEffect(() => {
        if (!images && !!order) {
            const payload: GlobalOrderDetailsPayload = {
                isMockRequest: config.mockRequest,
                orderId: Number(order.orderId),
                storeId: config.storeId,
                currency: config.currencyCode,
                pageNumber: 1,
                pageSize: 50,
            };
            getGlobalOrderDetailsImages(payload);
        }
    }, [images]);

    const mapOrderItemsAnalytics = (orderItems: IGlobalOrderDetails) => {
        let objects = {};
        if (orderItems.hasContactLenses && orderItems.orderStatus === "D") {
            orderItems.orderItems.forEach(item => {
                if (Array.isArray(item.partNumber)) {
                    let price = null;
                    let priceFull = null;

                    if (Array.isArray(item.orderItemType) && Array.isArray(item.quantity)) {
                        //It Means that we have Left and Right contact so to calculate the unit price we need to sum L and R quantity
                        let quantity = Number(item.quantity[0]) + Number(item.quantity[1])

                        price = Number(item.newPrice.replace("$ ", "").trim()) / quantity;
                        priceFull = Number(item.oldPrice.replace("$ ", "").trim()) / quantity;
                    }

                    item.partNumber.forEach((id, index) => {
                        objects[id] = {
                            "Status": item.orderItemInventoryStatus,
                            "Category": "CONTACT LENSES",
                            "LensType": "PLANO",
                            "Price": !!price ? price.toString() : item.newPrice.replace("$ ", ""),
                            "PriceFull": !!priceFull ? priceFull.toString() : item.oldPrice.replace("$ ", ""),
                            "Units": Number(item.quantity[index]).toString(),
                            "ModelName": item.frame,
                            "Brand": item.brand,
                            "ReorderSource": "Ecom",
                            "Image": item.imgUrl,
                            "Url": window.location.origin + item.productPageUrl
                        }
                    })
                } else {
                    let price = Number(item.newPrice.replace("$ ", "")) / Number(item.quantity);
                    let priceFull = Number(item.oldPrice.replace("$ ", "")) / Number(item.quantity);

                    objects[item.partNumber] = {
                        "Status": item.orderItemInventoryStatus,
                        "Category": "CONTACT LENSES",
                        "LensType": "PLANO",
                        "Price": price.toString(),
                        "PriceFull": priceFull.toString(),
                        "Units": Number(item.quantity).toString(),
                        "ModelName": item.frame,
                        "Brand": item.brand,
                        "ReorderSource": "Ecom",
                        "Image": item.imgUrl,
                        "Url": window.location.origin + item.productPageUrl
                    }
                }
            })

            setOrderItemsAnalytics(objects)
        }
    }

    const getGlobalOrderDetailsImages = (payload: GlobalOrderDetailsPayload) => {
        const { isMockRequest, ...rest } = payload;

        if (isMockRequest) {
            const globalOrderDetails =
                '/data/mocks/my_purchases_get_online_order_details_mock.json';

            getMockedFile(globalOrderDetails)
                .then(result => {
                    const mappedResultImages = mapGlobalOrderDetailsImages(result?.data);
                    const mappedResultStatus = mapGlobalOrderDetailsStatus(result?.data);
                    setImages(mappedResultImages);
                    setStatus(mappedResultStatus);
                })
                .catch(() => {
                    console.error('Error in get order detail for image');
                });
        } else {
            const globalOrderDetailsUrl = URLS.GET_GLOBAL_ORDER_DETAILS.replace(
                '{storeId}',
                rest.storeId.toString()
            )
                .replace('{orderId}', rest.orderId.toString())
                .replace('{currency}', rest.currency)
                .replace('{pageNumber}', rest.pageNumber.toString())
                .replace('{pageSize}', rest.pageSize.toString())
                .replace('{noCache}', new Date().getTime().toString());

            const config: AxiosRequestConfig = {
                method: 'GET',
                url: globalOrderDetailsUrl,
                headers: { 'Content-Type': 'application/json' },
            };

            axios(config)
                .then(result => {
                    const globalOrderDetails = mapGlobalOrderDetails(result?.data)
                    const mappedResultImages = mapGlobalOrderDetailsImages(result?.data);
                    const mappedResultStatus = mapGlobalOrderDetailsStatus(result?.data);

                    mapOrderItemsAnalytics(globalOrderDetails);
                    setImages(mappedResultImages);
                    setStatus(mappedResultStatus);
                })
                .catch(() => {
                    console.error('Error in get order detail for image');
                });
        }
    };

    const rendereOrderHistoryButtons = (
        orderId: string,
        hasContactLenses: string,
        ocrAddressId: string,
        orderStatus: string
    ): JSX.Element => {
        return (
            <div
                role="region"
                aria-label="Online order history view details button"
                key={uuidv4()}
                className={`${isMobile ? 'order-history-buttons' : 'mobile-order-history-buttons'}`}
            >
                {hasContactLenses === 'true' && orderStatus === 'D' && (
                    <button
                        className="primary-button button-size-small custom-mob"
                        data-element-id="OrderHistory_ReorderContacts"
                        onClick={() => {
                            handleContactLensesReorder(
                                +orderId,
                                +config.storeId,
                                +config.catalogId,
                                config.langId,
                                +ocrAddressId
                            )
                        }}
                    >
                        {getLabelFallback(
                            'MYPURCHASES_ONLINE_ORDERS_REORDER_CONTACT_LENSES_CTA_LABEL'
                        )}
                    </button>
                )}
                <button
                    className="primary-button-negative button-size-small custom-mob"
                    data-element-id="OrderHistory_OrderDetails"
                    onClick={() => navigate(`/order/${orderId}`)}
                >
                    {getLabelFallback('MYPURCHASES_ONLINE_ORDERS_VIEW_DETAILS_CTA_LABEL')}
                </button>
            </div>
        );
    };

    const renderWrapperHeader = (order: IOnlineOrderHistory): JSX.Element => {
        const { orderId, hasContactLenses, ocrAddressId, orderStatus, placedDate } = order;
        return (
            <>
                <div className="wrapper-summary-icon-container">
                    <OnlineOrder />
                    <span className="wrapper-summary-icon-container-text">{getLabelFallback('MYPROFILE_MY_ORDERS_ONLINE_LABEL')}</span>
                </div>
                <div
                    role="region"
                    aria-label="Online order history overview header"
                    key={uuidv4()}
                    className="wrapper-summary-header"
                >
                    <div className="wrapper-summary-header-order-number">
                        {getLabelFallback('MYPURCHASES_ONLINE_ORDERS_ORDER_NUMBER')?.replace(
                            '{ORDER_NUMBER}',
                            orderId
                        )}
                        <span className="wrapper-summary-header-date-order">
                            {getStringFromDate(new Date(placedDate), 'MM/dd/yyyy')}
                        </span>
                    </div>
                    {!isMobile &&
                        rendereOrderHistoryButtons(
                            orderId,
                            hasContactLenses,
                            ocrAddressId,
                            orderStatus
                        )}
                </div>
            </>
        );
    };

    const statusGroupToLabel = (
        statusGroup: string,
        isPrescriptionRequired: boolean
    ): string => {
        switch (statusGroup) {
            case 'SHIPPED':
                return 'MYPURCHASES_ONLINE_ORDERS_ORDER_STATUS_SHIPPED_LABEL_DESCRIPTION';
            case 'COMPLETED':
                return 'completed';
            case 'RETURNED':
                return 'MYPURCHASES_ONLINE_ORDERS_ORDER_STATUS_RETURNED_LABEL_DESCRIPTION';
            case 'CANCELLED':
                return 'cancelled';
            case 'READYFORPICKUP':
                return 'readypu';
            default: //PROCESSING
                return isPrescriptionRequired ? ''
                    : 'MYPURCHASES_ONLINE_ORDERS_ORDER_STATUS_PRESCRIPTION_LABEL_DESCRIPTION';
        }
    };

    /** The function below redirects the user to the section for providing the RX prescription */
    const goToProvidePrescriptionSection = (orderId: string, orderAddressEmail: string) => {
        window.open(
            config.callFromComponentToTO.myPurchases.onlineOrders.sendItNow
                .replace('{catalogId}', config.catalogId)
                .replace('{langId}', config.langId)
                .replace('{storeId}', config.storeId)
                .replace('{orderId}', orderId)
                .replace('{email}', encodeURIComponent(orderAddressEmail)),
            '_parent'
        );
    };

    const renderProductImage = element => {
        return (
            <BoxImage
                imageUrl={element.imgUrl}
                fallbackLabel={getLabelFallback('IMAGE_NON_AVAILABLE_LABEL')}
                imageAlt="Product image"
            />
        );
    };

    const renderWrapperBody = (order: IOnlineOrderHistory): JSX.Element => {
        const {
            orderId,
            orderAddressEmail,
            grandTotal,
            grandTotalCurrency,
            orderStatus,
            isPrescriptionRequired,
            isPrescriptionProvided,
            isExchangeOrder,
            allItemsHaveSameStatusGroupAndEdd,
            allItemsEdd,
            allOrderItemsStatusGroup,
        } = order;

        return (
            <div className="wrapper-summary-content-body">
                <div
                    role="region"
                    aria-label="Online order history overview body"
                    key={uuidv4()}
                    className="wrapper-summary-content-online-order"
                >
                    <div className="wrapper-summary-content-label">
                        {getLabelFallback('MYPURCHASES_ONLINE_ORDERS_ORDER_STATUS_LABEL')}
                    </div>
                    <div
                        className={
                            'wrapper-summary-content-value formatted' +
                            (!allItemsHaveSameStatusGroupAndEdd ? ' two-line' : '')
                        }
                    >
                        {orderStatus === 'M' && !isExchangeOrder && (
                            <>
                                {getLabelFallback(
                                    allItemsHaveSameStatusGroupAndEdd
                                        ? statusGroupToLabel(allOrderItemsStatusGroup, isPrescriptionRequired)
                                        : 'MYPURCHASES_ONLINE_ORDERS_ORDER_STATUS_DIFFERENT_LABEL'
                                )}

                                {allItemsHaveSameStatusGroupAndEdd &&
                                    !isPrescriptionRequired && isCTEnabledTO && allItemsEdd && (
                                        <>
                                            <div className='edd-separator'>|</div>
                                            <div className='edd-container'>
                                                <span className="edd-line">
                                                    {getLabelFallback(
                                                        'MYPURCHASES_ONLINE_ORDERS_ORDER_EDD_LABEL'
                                                    )}
                                                </span>
                                                <span className="edd-date">
                                                    {allItemsEdd.replaceAll("-", "/")}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                {!allItemsHaveSameStatusGroupAndEdd && (
                                    <span className="differnt-status">
                                        {getLabelFallback(
                                            'MYPURCHASES_ONLINE_ORDERS_ORDER_STATUS_DIFFERENT_2_LABEL'
                                        )}
                                    </span>
                                )}
                                {allItemsHaveSameStatusGroupAndEdd &&
                                    allOrderItemsStatusGroup !== 'SHIPPED' &&
                                    isPrescriptionRequired && (
                                        <div className="wrapper-summary-content-value-status-text">
                                            <Error />
                                            {!isMobile && (
                                                <>
                                                    <span className="status-text">{getLabelFallback('MYPURCHASES_ONLINE_ORDERS_ORDER_STATUS_PRESCRIPTION_REQUIRED_LABEL')}</span>
                                                    <a
                                                        className={`${!isMobile && 'send-prescription'}`}
                                                        onClick={() => {
                                                            /*dispatch(
                                                                getEncryptedOnlineOrderId({
                                                                    isMockRequest: config.mockRequest,
                                                                    storeId: config.storeId,
                                                                    orderId: Number(orderId),
                                                                })
                                                            )*/
                                                            goToProvidePrescriptionSection(
                                                                orderId,
                                                                orderAddressEmail
                                                            );
                                                        }}
                                                    >
                                                        {getLabelFallback(
                                                            'MYPURCHASES_ONLINE_ORDERS_ORDER_STATUS_PRESCRIPTION_LINK'
                                                        )}
                                                    </a>
                                                </>
                                            )}
                                            {isMobile && (
                                                <div className="wrapper-summary-content-value-status-text-and-icon-container">
                                                    <span className="status-text">{getLabelFallback('MYPURCHASES_ONLINE_ORDERS_ORDER_STATUS_PRESCRIPTION_REQUIRED_LABEL')} </span>
                                                    <a
                                                        className={`${!isMobile && 'send-prescription'}`}
                                                        onClick={() => {
                                                            /*dispatch(
                                                                getEncryptedOnlineOrderId({
                                                                    isMockRequest: config.mockRequest,
                                                                    storeId: config.storeId,
                                                                    orderId: Number(orderId),
                                                                })
                                                            )*/
                                                            goToProvidePrescriptionSection(
                                                                orderId,
                                                                orderAddressEmail
                                                            );
                                                        }}
                                                    >
                                                        {getLabelFallback(
                                                            'MYPURCHASES_ONLINE_ORDERS_ORDER_STATUS_PRESCRIPTION_LINK'
                                                        )}
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    )}
                            </>
                        )}
                        {orderStatus !== 'D' && orderStatus !== 'X' && isExchangeOrder && (
                            getLabelFallback('MYPURCHASES_ONLINE_ORDERS_ORDER_STATUS_EXCHANGED_LABEL')
                        )}
                        {orderStatus === 'D' &&
                            !isPrescriptionRequired &&
                            getLabelFallback(
                                !allItemsHaveSameStatusGroupAndEdd
                                    ? 'MYPURCHASES_ONLINE_ORDERS_ORDER_STATUS_DIFFERENT_LABEL'
                                    : allOrderItemsStatusGroup === 'RETURNED'
                                        ? 'MYPURCHASES_ONLINE_ORDERS_ORDER_STATUS_RETURNED_LABEL'
                                        : 'MYPURCHASES_ONLINE_ORDERS_ORDER_STATUS_SHIPPED_LABEL'
                            )}
                        {orderStatus === 'X' &&
                            !isPrescriptionRequired &&
                            getLabelFallback(
                                'MYPURCHASES_ONLINE_ORDERS_ORDER_STATUS_CANCELLED_LABEL'
                            )}
                    </div>
                    <div className="wrapper-summary-content-label">
                        {getLabelFallback('MYPURCHASES_ONLINE_ORDERS_ORDER_TOTAL_LABEL')}
                    </div>
                    <div className="wrapper-summary-content-value">{`${grandTotalCurrency} ${parseFloat(
                        grandTotal
                    ).toFixed(2)}`}</div>
                </div>
                {images && (
                    <div
                        role="region"
                        aria-label="Online order history overview body image"
                        key={uuidv4()}
                        className="wrapper-summary-content-body-images"
                    >
                        {images.map((element: { productPageUrl: any; }, index: string | number) => {
                            if (index <= MAX_IMAGE - 1) {
                                return (
                                    <div
                                        className={
                                            'wrapper-summary-content-body-images-inner number-' +
                                            index +
                                            (index === MAX_IMAGE - 1 ? ' last-element' : '')
                                        }
                                    >
                                        {!!element?.productPageUrl ? (
                                            <a href={element?.productPageUrl}>
                                                {renderProductImage(element)}
                                            </a>
                                        ) : (
                                            renderProductImage(element)
                                        )}
                                        {index === MAX_IMAGE - 1 && images.length > MAX_IMAGE && (
                                            <div className="overlay-number">
                                                +{' ' + (images.length - MAX_IMAGE)}
                                            </div>
                                        )}
                                    </div>
                                );
                            }
                        })}
                    </div>
                )}
            </div>
        );
    };

    const renderWrapperFooter = (): JSX.Element => {
        return (
            <div
                role="region"
                aria-label="Online order history overview footer"
                key={uuidv4()}
                className="wrapper-summary-footer"
            >
                <div className="primary-message medium align-left">
                    {getLabelFallback(
                        'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAILS_COMPLETED_1_SUGGEST'
                    )}
                    <a
                        href={'/to-us/contact-us'}
                    >
                        {getLabelFallback(
                            'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAILS_COMPLETED_SUGGEST_PLACEHOLDER'
                        )}
                    </a>
                    {getLabelFallback(
                        'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAILS_COMPLETED_2_SUGGEST'
                    )}
                    <a
                        href={'/to-us/returns'}
                    >
                        {getLabelFallback(
                            'MYPURCHASES_ONLINE_ORDERS_ORDER_DETAILS_COMPLETED_SUGGEST_PLACEHOLDER_RETURN'
                        )}
                    </a>
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="wrapper-summary">
                {renderWrapperHeader(order)}
                <div className="wrapper-summary-separator"></div>
                {renderWrapperBody(order)}
                {isMobile &&
                    rendereOrderHistoryButtons(
                        order.orderId,
                        order.hasContactLenses,
                        order.ocrAddressId,
                        order.orderStatus
                    )}
                {order.orderStatus === 'D' && renderWrapperFooter()}
            </div>
            <Modal
                isOpen={isOpenDeleteModal}
                close={onCloseModal}
                isCentered
                showCloseIcon={!isMobile}
                customStyle={isMobile ? 'eye-exams-modal-sm' : 'eye-exams-modal'}
            >
                <div className="eye-exams-delete-modal">
                    <div className="message">
                        {getLabelFallback('MYAPPOINTMENT_EYE_EXAMS_CANCEL_MODAL_MESSAGE')}
                    </div>
                    <div className="buttons-container">
                        <button
                            data-element-id="BookAnAppointmentOverlay_Cancel"
                            data-description="Confirm"
                            className={`primary-button button-size-medium ${isSubmitting ? 'loading' : ''
                                }`}
                            onClick={onModalSubmit}
                        >
                            {getLabelFallback(
                                'MYAPPOINTMENT_EYE_EXAMS_CANCEL_MODAL_CONFIRM_CTA_LABEL'
                            )}
                            {isSubmitting && <PreLoader />}
                        </button>
                        <button
                            data-element-id="BookAnAppointmentOverlay_Cancel"
                            data-description="Keep"
                            className="primary-button-negative button-size-medium"
                            onClick={onCloseModal}
                        >
                            {getLabelFallback(
                                'MYAPPOINTMENT_EYE_EXAMS_CANCEL_MODAL_CANCEL_CTA_LABEL'
                            )}
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
};
export default WrapperSummary;
